import { tempoApi } from '@/common/api/tempoApi';

interface FieldValueItem {
  value: unknown;
  id?: number;
  description?: string;
}

interface CreateItemFieldValueRequest {
  fieldValue: FieldValueItem[];
}

interface DeleteItemFieldValueRequest {
  fieldValueIds: number[];
}

interface FieldValue<T> {
  id: number;
  value: T;
  description?: string;
}

export interface ItemFieldValue {
  fieldId: number;
  fieldType: string;
  itemId: number;
  fieldValue: FieldValue<unknown>[];
  supportsMultipleItems: boolean;
  fieldName: string;
}

interface LinkValue {
  url: string;
  description?: string;
}

const isLinkValue = (value: unknown): value is LinkValue =>
  value !== null && typeof value === 'object' && 'url' in value;

const toFieldValueItem = (value: unknown, id?: number): FieldValueItem => {
  const baseItem = isLinkValue(value)
    ? { value: value.url, description: value.description }
    : { value };
  return id !== undefined ? { ...baseItem, id } : baseItem;
};

const upsert = async (
  itemId: number,
  fieldId: number,
  value: unknown,
  valueId?: number,
): Promise<ItemFieldValue> => {
  const request: CreateItemFieldValueRequest = {
    fieldValue: [toFieldValueItem(value, valueId)],
  };

  return await tempoApi()
    .post(`api/v1/item-store/items/${itemId}/field-values/${fieldId}`, {
      json: request,
    })
    .json<ItemFieldValue>();
};

const upsertMany = async (
  itemId: number,
  fieldId: number,
  values: unknown[],
): Promise<ItemFieldValue> => {
  const request: CreateItemFieldValueRequest = {
    fieldValue: values.map((v) => toFieldValueItem(v)),
  };

  return await tempoApi()
    .post(`api/v1/item-store/items/${itemId}/field-values/${fieldId}`, {
      json: request,
    })
    .json<ItemFieldValue>();
};

const deleteFieldValues = async (
  itemId: number,
  fieldId: number,
  valueIds: number[],
): Promise<void> => {
  const request: DeleteItemFieldValueRequest = { fieldValueIds: valueIds };
  await tempoApi().delete(`api/v1/item-store/items/${itemId}/field-values/${fieldId}`, {
    json: request,
  });
};

export const FieldValuesApi = {
  upsert,
  upsertMany,
  delete: deleteFieldValues,
} as const;
